import React, { useState, useEffect, useRef } from 'react';
import { ConfirmModal } from '../common/ConfirmModal';

export const LoginPage = ({ onLogin, onSendCode, onLoginWithCode }) => {
  // 'sms' = celular + código (método principal) · 'password' = usuario/contraseña
  const [mode, setMode] = useState('sms');

  // --- Login clásico ---
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // --- Login por SMS ---
  const [smsStep, setSmsStep] = useState('phone'); // 'phone' | 'code'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const cooldownRef = useRef(null);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Temporizador del cooldown para reenviar el código
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(cooldownRef.current);
  }, [cooldown]);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onLogin(username, password);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  // El submit del número abre el modal de confirmación; el envío real lo hace doSendCode.
  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (!phone) return;
    setError('');
    setInfo('');
    setShowConfirm(true);
  };

  const doSendCode = async () => {
    setShowConfirm(false);
    setError('');
    setInfo('');
    setLoading(true);
    const result = await onSendCode(phone);
    if (result.success) {
      setSmsStep('code');
      setInfo('Te enviamos un código de 4 dígitos por SMS.');
      setCooldown(60);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await onLoginWithCode(phone, code);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    setError('');
    setInfo('');
    setLoading(true);
    const result = await onSendCode(phone);
    if (result.success) {
      setInfo('Te enviamos un código nuevo por SMS.');
      setCooldown(60);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const subtleLink = {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#697077',
    fontSize: '0.8rem',
    textDecoration: 'underline',
    cursor: 'pointer'
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-brand">
            <div className="login-logo">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="14" width="4" height="7" rx="0.5" fill="white" opacity="0.4"/>
                <rect x="8" y="10" width="4" height="11" rx="0.5" fill="white" opacity="0.6"/>
                <rect x="14" y="6" width="4" height="15" rx="0.5" fill="white" opacity="0.8"/>
                <polyline points="2 12 7 8 13 10 22 3" stroke="white" strokeWidth="2.2"/>
                <polyline points="17 3 22 3 22 8" stroke="white" strokeWidth="2.2"/>
              </svg>
            </div>
            <span className="brand-name-login">CostProject</span>
          </div>
        </div>

        {error && (
          <div className="login-error">
            <span className="error-icon">{'✕'}</span>
            {error}
          </div>
        )}
        {info && !error && (
          <div className="login-error" style={{ background: '#defbe6', color: '#0e6027' }}>
            <span className="error-icon">{'✓'}</span>
            {info}
          </div>
        )}

        {mode === 'sms' && smsStep === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="phone">Numero de celular</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ej: +573001234567"
                disabled={loading}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !phone}
            >
              {loading ? 'Enviando...' : 'Enviar codigo'}
            </button>
          </form>
        )}

        {mode === 'sms' && smsStep === 'code' && (
          <form onSubmit={handleVerifyCode} className="login-form">
            <div className="login-field">
              <label htmlFor="code">Codigo de 4 digitos</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="----"
                disabled={loading}
                autoFocus
                style={{ letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.3rem' }}
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || code.length !== 4}
            >
              {loading ? 'Verificando...' : 'Ingresar'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', fontSize: '0.85rem' }}>
              <button
                type="button"
                onClick={() => { setSmsStep('phone'); setCode(''); setError(''); setInfo(''); }}
                style={{ background: 'none', border: 'none', color: '#0f62fe', cursor: 'pointer', padding: 0 }}
              >
                Cambiar numero
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || loading}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: cooldown > 0 ? '#a2a9b0' : '#0f62fe',
                  cursor: cooldown > 0 ? 'default' : 'pointer'
                }}
              >
                {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar codigo'}
              </button>
            </div>
          </form>
        )}

        {mode === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="username">Usuario o correo</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu usuario o correo"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">Contrasena</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contrasena"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !username || !password}
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        )}

        {/* Conmutador de método: SMS es el principal. El acceso con usuario y
            contraseña es un ícono discreto para que casi todos usen el SMS. */}
        {mode === 'sms' ? (
          <button
            type="button"
            className="login-alt-btn"
            onClick={() => switchMode('password')}
            title="Ingresar con usuario y contraseña"
            aria-label="Ingresar con usuario y contraseña"
          >
            <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
              <path d="M21 2a8.998 8.998 0 0 0-8.612 11.612L2 24v6h6l10.388-10.388A9 9 0 1 0 21 2Zm0 16a7.013 7.013 0 0 1-2.032-.302l-1.147-.348-.847.847-3.181 3.181L12.414 20 11 21.414l1.379 1.379-1.586 1.586L9.414 23 8 24.414l1.379 1.379L7.172 28H4v-3.172l9.802-9.802.848-.847-.348-1.147A7 7 0 1 1 21 18Z" />
              <circle cx="22" cy="10" r="2" />
            </svg>
          </button>
        ) : (
          <div className="login-alt-volver">
            <button type="button" style={subtleLink} onClick={() => switchMode('sms')}>
              Volver al ingreso con código SMS
            </button>
          </div>
        )}
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Confirmar envio de codigo"
          message={`Enviaremos un codigo de verificacion por SMS al numero ${phone}. ¿Deseas continuar?`}
          confirmLabel="Enviar codigo"
          cancelLabel="Cancelar"
          onConfirm={doSendCode}
          onClose={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
};
